import React, { useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ConfigurationPanel = ({ config, onConfigChange }) => {
  const dataSourceOptions = [
    { value: 'ipsos', label: 'Ipsos Global', description: 'Global market research data' },
    { value: 'ons', label: 'ONS (UK)', description: 'Office for National Statistics' },
    { value: 'undata', label: 'UNData', description: 'United Nations Statistics' },
    { value: 'eurostat', label: 'Eurostat', description: 'European Union Statistics' },
    { value: 'census', label: 'US Census', description: 'United States Census Bureau' }
  ];

  const regionOptions = [
    { value: 'global', label: 'Global' },
    { value: 'north-america', label: 'North America' },
    { value: 'europe', label: 'Europe' },
    { value: 'asia-pacific', label: 'Asia Pacific' },
    { value: 'latin-america', label: 'Latin America' },
    { value: 'middle-east-africa', label: 'Middle East & Africa' }
  ];

  const outputFormatOptions = [
    { value: 'dashboard', label: 'Interactive Dashboard' },
    { value: 'pdf-report', label: 'PDF Report' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'powerbi', label: 'Power BI Dataset' },
    { value: 'api', label: 'API Endpoint' }
  ];

  const handleInputChange = useCallback((field, value) => {
    onConfigChange(prevConfig => ({
      ...prevConfig,
      [field]: value
    }));
  }, [onConfigChange]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-foreground mb-2">
          Configuration Settings
        </h2>
        <p className="text-text-secondary">
          Customize your agent's data collection and analysis parameters
        </p>
      </div>
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Data Sources */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Icon name="Database" size={20} className="text-primary" />
              <h3 className="text-lg font-semibold text-card-foreground">
                Data Sources
              </h3>
            </div>
            
            <Select
              label="Primary Data Source"
              description="Select the main data provider for analysis"
              options={dataSourceOptions}
              value={config?.dataSource}
              onChange={(value) => handleInputChange('dataSource', value)}
              searchable
              required
            />

            <Select
              label="Target Region"
              description="Geographic scope for data collection"
              options={regionOptions}
              value={config?.region}
              onChange={(value) => handleInputChange('region', value)}
              required
            />
          </div>

          {/* Analysis Parameters */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Icon name="Settings" size={20} className="text-primary" />
              <h3 className="text-lg font-semibold text-card-foreground">
                Analysis Parameters
              </h3>
            </div>

            <Input
              id="research-topic-input"
              label="Research Topic"
              type="text"
              placeholder="e.g., Consumer sentiment on electric vehicles"
              description="Main focus area for data analysis"
              value={config?.topic || ''}
              onChange={(e) => handleInputChange('topic', e?.target?.value)}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                id="start-date-input"
                label="Start Date"
                type="date"
                value={config?.startDate || ''}
                onChange={(e) => handleInputChange('startDate', e?.target?.value)}
                required
              />
              
              <Input
                id="end-date-input"
                label="End Date"
                type="date"
                value={config?.endDate || ''}
                onChange={(e) => handleInputChange('endDate', e?.target?.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* Output Preferences */}
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="FileOutput" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-card-foreground">
              Output Preferences
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Output Format"
              description="Choose how you want to receive results"
              options={outputFormatOptions}
              value={config?.outputFormat}
              onChange={(value) => handleInputChange('outputFormat', value)}
              multiple
              required
            />

            <div className="space-y-3">
              <label className="text-sm font-medium text-card-foreground">
                Analysis Options
              </label>
              
              <div className="space-y-2">
                <Checkbox
                  label="Include sentiment analysis"
                  checked={config?.includeSentiment}
                  onChange={(e) => handleInputChange('includeSentiment', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Generate trend predictions"
                  checked={config?.includeTrends}
                  onChange={(e) => handleInputChange('includeTrends', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Cross-reference demographics"
                  checked={config?.includeDemographics}
                  onChange={(e) => handleInputChange('includeDemographics', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Enable real-time updates"
                  checked={config?.enableRealTime}
                  onChange={(e) => handleInputChange('enableRealTime', e?.target?.checked)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Settings */}
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Sliders" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-card-foreground">
              Advanced Settings
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              id="sample-size-input"
              label="Sample Size"
              type="number"
              placeholder="10000"
              description="Minimum data points to collect"
              value={config?.sampleSize || ''}
              onChange={(e) => handleInputChange('sampleSize', e?.target?.value)}
              min="100"
              max="1000000"
            />

            <Input
              id="confidence-level-input"
              label="Confidence Level (%)"
              type="number"
              placeholder="95"
              description="Statistical confidence threshold"
              value={config?.confidenceLevel || ''}
              onChange={(e) => handleInputChange('confidenceLevel', e?.target?.value)}
              min="80"
              max="99"
            />

            <Input
              id="update-frequency-input"
              label="Update Frequency"
              type="number"
              placeholder="24"
              description="Hours between data refreshes"
              value={config?.updateFrequency || ''}
              onChange={(e) => handleInputChange('updateFrequency', e?.target?.value)}
              min="1"
              max="168"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPanel;